/**
 * Subscription Plans Management Page
 *
 * Platform-level subscription plan management for superadmins
 * Requires permissions: platform.subscriptions.view, platform.subscriptions.manage
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  PlusIcon,
  ChartBarIcon,
  LockClosedIcon,
  UsersIcon,
  DocumentTextIcon,
  ClockIcon,
  CreditCardIcon,
  PencilIcon,
  UserPlusIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { PageTransition, PageTransitionItem } from '@/components/ui/PageTransition';
import { UsageProgressBar } from '@/components/subscriptions/UsageProgressBar';
import { CreatePlanModal } from '@/components/subscriptions/CreatePlanModal';
import { BulkAssignPlanModal } from '@/components/subscriptions/BulkAssignPlanModal';
import { AssignPlanFromCatalogModal } from '@/components/subscriptions/AssignPlanFromCatalogModal';
import { useSubscriptionStore, SubscriptionPlan, Subscription } from '@/stores/subscriptionStore';
import { DataTable, ColumnDefinition, SortState, ActionDefinition } from '@/components/shared/DataTable';
import { BulkActionsBar, BulkAction } from '@/components/shared/BulkActionsBar';
import { TIER_BADGE_CLASSES, TIER_DOT_CLASSES, tierClasses, tierRank } from '../utils/planTiers';

// Tabs
type TabType = 'plans' | 'subscriptions' | 'analytics';

export const SubscriptionPlansPage: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('plans');
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  // The plan catalog is scoped per caller; the cross-organization subscription list and the revenue
  // it totals are not, and stay with platform staff.
  const [canReadAllSubscriptions, setCanReadAllSubscriptions] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);

  // Modal states for plan assignment
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [isAssignFromPlanModalOpen, setIsAssignFromPlanModalOpen] = useState(false);
  const [selectedPlanForAssign, setSelectedPlanForAssign] = useState<SubscriptionPlan | null>(null);
  const [selectedOrgsForAssign, setSelectedOrgsForAssign] = useState<{ id: string; name: string; currentPlanId?: string; currentPlanName?: string }[]>([]);

  const {
    plans,
    plansLoading,
    plansError,
    allSubscriptions,
    allSubscriptionsLoading,
    fetchPlans,
    fetchAllSubscriptions
  } = useSubscriptionStore();

  // Check permission on mount
  useEffect(() => {
    const checkPermission = async () => {
      if (!user) {
        setHasPermission(false);
        return;
      }

      // Superadmin has platform.* which includes subscription permissions
      if (user.role === 'superadmin') {
        setHasPermission(true);
        setCanReadAllSubscriptions(true);
        return;
      }

      // Check explicit permission
      const permissions = user.permissions || [];
      const platformWide = permissions.some(p =>
        p === 'platform.subscriptions.view' ||
        p === 'platform.subscriptions.manage' ||
        p === 'platform.*'
      );
      setHasPermission(platformWide || permissions.includes('platform.plans.view'));
      setCanReadAllSubscriptions(platformWide);
    };

    if (!authLoading) {
      checkPermission();
    }
  }, [user, authLoading]);

  // Fetch data when permission is granted
  useEffect(() => {
    if (hasPermission) {
      fetchPlans();
    }
    // Skipped rather than allowed to 403: the call is unscoped, so a scoped caller has no
    // narrower version of it to make.
    if (hasPermission && canReadAllSubscriptions) {
      fetchAllSubscriptions();
    }
  }, [hasPermission, canReadAllSubscriptions, fetchPlans, fetchAllSubscriptions]);

  // Loading state
  if (authLoading || hasPermission === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-indigo-50/20 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Access denied
  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-indigo-50/20 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 max-w-md text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <LockClosedIcon className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600 mb-6">
            You don't have permission to view subscription plans. This page requires
            the <code className="bg-slate-100 px-2 py-0.5 rounded text-sm">platform.plans.view</code> permission.
          </p>
          <button
            onClick={() => navigate('/management')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Back to Management
          </button>
        </motion.div>
      </div>
    );
  }

  // Calculate summary stats
  const activePlans = plans.filter(p => p.isActive);
  const activeSubscriptions = allSubscriptions.filter(s => s.status === 'active' || s.status === 'trialing');
  const totalMRR = activeSubscriptions.reduce((sum, sub) => {
    const plan = plans.find(p => p.planId === sub.planId);
    return sum + (plan?.pricing.basePrice || 0);
  }, 0);

  // Subscription counts and revenue come from the unscoped list, so they are shown only to the
  // callers who can load it. A zeroed MRR tile reads as "no revenue", not as "not your data".
  const visibleTabs: TabType[] = canReadAllSubscriptions
    ? ['plans', 'subscriptions', 'analytics']
    : ['plans'];

  const permissionsHeld = user?.permissions || [];
  const holds = (permission: string) =>
    user?.role === 'superadmin' || permissionsHeld.includes(permission) || permissionsHeld.includes('platform.*');

  const canAuthorPlans = holds('platform.plans.author');
  const canAssignPlans = holds('platform.subscriptions.assign');

  /**
   * Mirrors `canEditPlan` on the server, which is authoritative. A platform-global plan is
   * excluded for a scoped caller even though they may assign it: editing it reprices every other
   * enterprise using it.
   */
  const canEditOwnedPlan = (plan: SubscriptionPlan) => {
    if (!canAuthorPlans) return false;
    if (user?.role === 'superadmin') return true;
    if (!plan.enterpriseId || !user?.enterpriseId) return false;
    return plan.enterpriseId === user.enterpriseId;
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-indigo-50/20">
        {/* Header */}
        <div className="bg-white/60 backdrop-blur-sm border-b border-slate-200/50">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <PageTransitionItem>
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl font-bold text-slate-800">Subscription Management</h1>
                  <p className="text-slate-600 mt-1">Manage subscription plans and organization subscriptions</p>
                </div>
                {canAuthorPlans && (
                  <button
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    onClick={() => {
                      setEditingPlan(null);
                      setIsCreateModalOpen(true);
                    }}
                  >
                    <PlusIcon className="w-5 h-5" />
                    New Plan
                  </button>
                )}
              </div>
            </PageTransitionItem>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          <PageTransitionItem>
            <div className={`grid grid-cols-1 gap-4 mb-6 ${canReadAllSubscriptions ? 'md:grid-cols-4' : 'md:grid-cols-1'}`}>
              <StatCard
                icon={<DocumentTextIcon className="w-6 h-6" />}
                label="Active Plans"
                value={activePlans.length.toString()}
                color="purple"
              />
              {canReadAllSubscriptions && (
                <>
                  <StatCard
                    icon={<BuildingOfficeIcon className="w-6 h-6" />}
                    label="Active Subscriptions"
                    value={activeSubscriptions.length.toString()}
                    color="blue"
                  />
                  <StatCard
                    icon={<CurrencyDollarIcon className="w-6 h-6" />}
                    label="Total MRR"
                    value={`$${totalMRR.toLocaleString()}`}
                    color="emerald"
                  />
                  <StatCard
                    icon={<ChartBarIcon className="w-6 h-6" />}
                    label="Avg. per Org"
                    value={activeSubscriptions.length > 0 ? `$${Math.round(totalMRR / activeSubscriptions.length)}` : '$0'}
                    color="amber"
                  />
                </>
              )}
            </div>
          </PageTransitionItem>

          {/* Tabs */}
          <PageTransitionItem>
            <div className="flex space-x-1 bg-white/60 backdrop-blur-sm rounded-lg p-1 mb-6 w-fit">
              {visibleTabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? 'bg-purple-600 text-white'
                      : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </PageTransitionItem>

          {/* Tab Content */}
          <PageTransitionItem>
            {activeTab === 'plans' && (
              <PlansTabContent
                plans={plans}
                allSubscriptions={allSubscriptions}
                loading={plansLoading}
                error={plansError}
                onEditPlan={(plan) => {
                  setEditingPlan(plan);
                  setIsCreateModalOpen(true);
                }}
                onCreatePlan={() => {
                  setEditingPlan(null);
                  setIsCreateModalOpen(true);
                }}
                onAssignPlan={(plan) => {
                  setSelectedPlanForAssign(plan);
                  setIsAssignFromPlanModalOpen(true);
                }}
                canEditPlan={canEditOwnedPlan}
                canCreatePlan={canAuthorPlans}
                canAssignPlan={canAssignPlans}
              />
            )}
            {activeTab === 'subscriptions' && canReadAllSubscriptions && (
              <SubscriptionsTabContent
                subscriptions={allSubscriptions}
                plans={plans}
                loading={allSubscriptionsLoading}
                onAssignSingle={(sub) => {
                  const plan = plans.find(p => p.planId === sub.planId);
                  setSelectedOrgsForAssign([{
                    id: sub.organizationId,
                    name: sub.organizationName || 'Unknown',
                    currentPlanId: sub.planId,
                    currentPlanName: plan?.name
                  }]);
                  setIsBulkAssignModalOpen(true);
                }}
                onAssignBulk={(subs) => {
                  const orgs = subs.map(sub => {
                    const plan = plans.find(p => p.planId === sub.planId);
                    return {
                      id: sub.organizationId,
                      name: sub.organizationName || 'Unknown',
                      currentPlanId: sub.planId,
                      currentPlanName: plan?.name
                    };
                  });
                  setSelectedOrgsForAssign(orgs);
                  setIsBulkAssignModalOpen(true);
                }}
              />
            )}
            {activeTab === 'analytics' && canReadAllSubscriptions && (
              <AnalyticsTabContent
                subscriptions={allSubscriptions}
                plans={plans}
                loading={allSubscriptionsLoading || plansLoading}
              />
            )}
          </PageTransitionItem>
        </div>

        {/* Create/Edit Plan Modal */}
        <CreatePlanModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setEditingPlan(null);
          }}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            setEditingPlan(null);
          }}
          editingPlan={editingPlan}
        />

        {/* Bulk Assign Plan Modal (from Subscriptions tab) */}
        <BulkAssignPlanModal
          isOpen={isBulkAssignModalOpen}
          onClose={() => {
            setIsBulkAssignModalOpen(false);
            setSelectedOrgsForAssign([]);
          }}
          organizations={selectedOrgsForAssign}
          onSuccess={() => {
            fetchAllSubscriptions();
          }}
        />

        {/* Assign Plan From Catalog Modal (from Plans tab) */}
        {selectedPlanForAssign && (
          <AssignPlanFromCatalogModal
            isOpen={isAssignFromPlanModalOpen}
            onClose={() => {
              setIsAssignFromPlanModalOpen(false);
              setSelectedPlanForAssign(null);
            }}
            plan={selectedPlanForAssign}
            onSuccess={() => {
              fetchAllSubscriptions();
            }}
          />
        )}
      </div>
    </PageTransition>
  );
};

// Stat Card Component
const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  color: 'purple' | 'blue' | 'emerald' | 'amber';
}> = ({ icon, label, value, color }) => {
  const colorClasses = {
    purple: 'bg-purple-100 text-purple-600',
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600'
  };

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-slate-200/50">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-slate-600">{label}</p>
          <p className="text-xl font-bold text-slate-800">{value}</p>
        </div>
      </div>
    </div>
  );
};

// Tier Badge Component
const TierBadge: React.FC<{ tier: SubscriptionPlan['tier'] }> = ({ tier }) => {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tierClasses(
        TIER_BADGE_CLASSES,
        tier,
        'bg-gray-100 text-gray-700'
      )}`}
    >
      {tier.charAt(0).toUpperCase() + tier.slice(1)}
    </span>
  );
};

// Plans Tab Content - DataTable Version
const PlansTabContent: React.FC<{
  plans: SubscriptionPlan[];
  allSubscriptions: Subscription[];
  loading: boolean;
  error: string | null;
  onEditPlan: (plan: SubscriptionPlan) => void;
  onCreatePlan: () => void;
  onAssignPlan: (plan: SubscriptionPlan) => void;
  canEditPlan: (plan: SubscriptionPlan) => boolean;
  canCreatePlan: boolean;
  canAssignPlan: boolean;
}> = ({
  plans,
  allSubscriptions,
  loading,
  error,
  onEditPlan,
  onCreatePlan,
  onAssignPlan,
  canEditPlan,
  canCreatePlan,
  canAssignPlan,
}) => {
  const [sortState, setSortState] = useState<SortState>({
    column: 'name',
    direction: 'asc',
  });

  // Count subscriptions for each plan
  const subscriptionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allSubscriptions.forEach(sub => {
      if (sub.planId) {
        counts[sub.planId] = (counts[sub.planId] || 0) + 1;
      }
    });
    return counts;
  }, [allSubscriptions]);

  // Sorted plans
  const sortedPlans = useMemo(() => {
    if (!sortState.column || !sortState.direction) return plans;

    return [...plans].sort((a, b) => {
      let aVal: string | number | boolean;
      let bVal: string | number | boolean;

      switch (sortState.column) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'tier': {
          aVal = tierRank(a.tier);
          bVal = tierRank(b.tier);
          break;
        }
        case 'setupFee':
          aVal = a.pricing.setupFeePerClinicalUser || 0;
          bVal = b.pricing.setupFeePerClinicalUser || 0;
          break;
        case 'clinicalPrice':
          aVal = a.pricing.monthlyPerClinicalUser || a.pricing.basePrice || 0;
          bVal = b.pricing.monthlyPerClinicalUser || b.pricing.basePrice || 0;
          break;
        case 'staffPrice':
          aVal = a.pricing.monthlyPerStaffUser || 0;
          bVal = b.pricing.monthlyPerStaffUser || 0;
          break;
        case 'subscriptions':
          aVal = subscriptionCounts[a.planId] || 0;
          bVal = subscriptionCounts[b.planId] || 0;
          break;
        case 'isActive':
          aVal = a.isActive ? 1 : 0;
          bVal = b.isActive ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortState.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortState.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [plans, sortState, subscriptionCounts]);

  // DataTable column definitions
  const planColumns: ColumnDefinition<SubscriptionPlan>[] = useMemo(() => [
    {
      id: 'plan',
      header: 'Plan',
      sortable: true,
      sortKey: 'name',
      width: '28%',
      isPrimary: true,
      accessor: (plan) => (
        <div className="flex items-center gap-3">
          <TierBadge tier={plan.tier} />
          {/* No width cap: under fixed layout the cell cannot widen its column, so truncation alone
              keeps a long name inside it. */}
          <div className="min-w-0">
            <div className="font-medium text-gray-900 truncate">{plan.name}</div>
            {plan.description && (
              <div className="text-xs text-gray-500 truncate">{plan.description}</div>
            )}
          </div>
        </div>
      )
    },
    {
      id: 'setupFee',
      header: 'Setup Fee',
      sortable: true,
      sortKey: 'setupFee',
      width: '10%',
      align: 'right',
      headerClassName: 'pr-8',
      cellClassName: 'pr-8',
      hideOnTablet: true,
      accessor: (plan) => {
        const setupFee = plan.pricing.setupFeePerClinicalUser;
        if (!setupFee || setupFee === 0) {
          return <span className="text-gray-400">—</span>;
        }
        return (
          <span className="font-medium text-amber-700">${setupFee}</span>
        );
      }
    },
    {
      id: 'clinicalPrice',
      header: 'Clinical/mo',
      sortable: true,
      sortKey: 'clinicalPrice',
      width: '11%',
      align: 'right',
      headerClassName: 'pr-8',
      cellClassName: 'pr-8',
      accessor: (plan) => {
        const price = plan.pricing.monthlyPerClinicalUser || plan.pricing.basePrice || 0;
        const isPerUser = (plan.pricing.monthlyPerClinicalUser ?? 0) > 0;
        return (
          <div className="text-right">
            <span className="font-semibold text-gray-900">${price.toLocaleString()}</span>
            {isPerUser && <span className="text-xs text-gray-500 block">/user</span>}
          </div>
        );
      }
    },
    {
      id: 'staffPrice',
      header: 'Staff/mo',
      sortable: true,
      sortKey: 'staffPrice',
      width: '10%',
      align: 'right',
      hideOnTablet: true,
      accessor: (plan) => {
        const price = plan.pricing.monthlyPerStaffUser;
        if (!price || price === 0) {
          return <span className="text-gray-400">—</span>;
        }
        return (
          <span className="text-gray-600">${price}</span>
        );
      }
    },
    {
      id: 'limits',
      header: 'Limits/User',
      width: '11%',
      hideOnTablet: true,
      accessor: (plan) => {
        const encountersPerUser = plan.included.encountersPerClinicalUser;
        const minutesPerUser = plan.included.minutesPerClinicalUser;

        if (!encountersPerUser && !minutesPerUser) {
          // Legacy mode - show org-level limits
          const encounters = plan.included.encountersPerMonth === -1 ? '∞' : plan.included.encountersPerMonth;
          const minutes = plan.included.transcriptionMinutes === -1 ? '∞' : plan.included.transcriptionMinutes;
          return (
            <div className="text-xs text-gray-500">
              <div>{encounters} enc/mo</div>
              <div>{minutes} min/mo</div>
            </div>
          );
        }

        return (
          <div className="text-xs text-gray-600">
            <div>{encountersPerUser === -1 ? '∞' : encountersPerUser} enc</div>
            <div>{minutesPerUser === -1 ? '∞' : (minutesPerUser || 0)} min</div>
          </div>
        );
      }
    },
    {
      id: 'subscriptions',
      header: 'Subs',
      sortable: true,
      sortKey: 'subscriptions',
      // The cell holds a pill, not a bare number: 48px of cell padding plus the pill's own leaves a
      // narrower column clipping the count it is meant to show.
      width: '9%',
      align: 'center',
      accessor: (plan) => {
        const count = subscriptionCounts[plan.planId] || 0;
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            count > 0 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {count}
          </span>
        );
      }
    },
    {
      id: 'owner',
      header: 'Owner',
      sortable: true,
      sortKey: 'enterpriseName',
      width: '12%',
      hideOnTablet: true,
      // Plan names are not unique across enterprises, so without this two rows can be identical.
      accessor: (plan) => plan.enterpriseId
        ? <span className="text-xs text-slate-700">{plan.enterpriseName || plan.enterpriseId}</span>
        : <span className="text-xs text-slate-500">Platform</span>
    },
    {
      id: 'status',
      header: 'Active',
      sortable: true,
      sortKey: 'isActive',
      width: '7%',
      align: 'center',
      accessor: (plan) => plan.isActive
        ? <CheckCircleIcon className="h-5 w-5 text-green-500 mx-auto" />
        : <XCircleIcon className="h-5 w-5 text-red-400 mx-auto" />
    }
  ], [subscriptionCounts]);

  // Row actions
  const rowActions: ActionDefinition<SubscriptionPlan>[] = useMemo(() => [
    {
      id: 'edit',
      icon: <PencilIcon className="h-4 w-4" />,
      tooltip: 'Edit Plan',
      onClick: (plan) => onEditPlan(plan),
      // Per row, not per caller: a platform-global plan is assignable and not editable, so the
      // control has to disappear on some rows and stay on others.
      show: (plan) => canEditPlan(plan),
      variant: 'primary' as const,
    },
    {
      id: 'assign',
      icon: <UserPlusIcon className="h-4 w-4" />,
      tooltip: 'Assign to Organizations',
      onClick: (plan) => onAssignPlan(plan),
      show: () => canAssignPlan,
      variant: 'default' as const,
    }
  ], [onEditPlan, onAssignPlan, canEditPlan, canAssignPlan]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!loading && plans.length === 0) {
    return (
      <div className="bg-white/60 backdrop-blur-sm rounded-xl p-12 text-center">
        <DocumentTextIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-800 mb-2">No Plans Yet</h3>
        <p className="text-slate-600 mb-4">
          {canCreatePlan
            ? 'Create your first subscription plan to get started.'
            : 'No plans are available to you yet.'}
        </p>
        {canCreatePlan && (
          <button
            onClick={onCreatePlan}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Create Plan
          </button>
        )}
      </div>
    );
  }

  return (
    <DataTable
      data={sortedPlans}
      columns={planColumns}
      actions={rowActions}
      loading={loading}
      sortable={true}
      sortState={sortState}
      onSortChange={setSortState}
      getRowId={(plan) => plan.id || plan.planId}
      emptyMessage="No subscription plans found."
      emptyIcon={<DocumentTextIcon className="h-12 w-12 text-slate-300" />}
      hoverable
      striped={false}
      ariaLabel="Subscription plans table"
      // Auto layout reads the column widths as hints and grows past the container to fit content,
      // which pushes the last column under the sticky actions cell. Fixed layout honours them.
      // The floor keeps every column readable at its share of the width; the card scrolls
      // horizontally below it rather than abbreviating a name.
      tableClassName="table-fixed min-w-[1200px]"
      // Two icon buttons, not the five the default allows for.
      actionsWidth="104px"
    />
  );
};

// Subscriptions Tab Content
const SubscriptionsTabContent: React.FC<{
  subscriptions: Subscription[];
  plans: SubscriptionPlan[];
  loading: boolean;
  onAssignSingle: (subscription: Subscription) => void;
  onAssignBulk: (subscriptions: Subscription[]) => void;
}> = ({ subscriptions, plans, loading, onAssignSingle, onAssignBulk }) => {
  const [sortState, setSortState] = React.useState<SortState>({
    column: 'organizationName',
    direction: 'asc',
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Sorted subscriptions
  const sortedSubscriptions = useMemo(() => {
    if (!sortState.column || !sortState.direction) return subscriptions;

    return [...subscriptions].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortState.column) {
        case 'organizationName':
          aVal = (a.organizationName || '').toLowerCase();
          bVal = (b.organizationName || '').toLowerCase();
          break;
        case 'planId': {
          const planA = plans.find(p => p.planId === a.planId);
          const planB = plans.find(p => p.planId === b.planId);
          aVal = (planA?.name || a.planId || '').toLowerCase();
          bVal = (planB?.name || b.planId || '').toLowerCase();
          break;
        }
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'usage':
          aVal = a.usage?.currentEncountersThisMonth ?? 0;
          bVal = b.usage?.currentEncountersThisMonth ?? 0;
          break;
        case 'periodEnds':
          aVal = a.period?.currentPeriodEnd ? new Date(a.period.currentPeriodEnd).getTime() : 0;
          bVal = b.period?.currentPeriodEnd ? new Date(b.period.currentPeriodEnd).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortState.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortState.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [subscriptions, plans, sortState]);

  // Row actions for subscriptions
  const rowActions: ActionDefinition<Subscription>[] = useMemo(() => [
    {
      id: 'assignPlan',
      icon: <CreditCardIcon className="h-4 w-4" />,
      tooltip: 'Assign Plan',
      onClick: (sub) => onAssignSingle(sub),
      variant: 'primary' as const,
    },
  ], [onAssignSingle]);

  // Bulk actions
  const bulkActions: BulkAction<Subscription>[] = useMemo(() => [
    {
      id: 'assignPlan',
      label: 'Assign Plan',
      icon: <CreditCardIcon className="h-4 w-4" />,
      onClick: async (ids: string[]) => {
        const selectedSubs = subscriptions.filter(s => ids.includes(s.organizationId));
        onAssignBulk(selectedSubs);
      },
      variant: 'primary' as const,
    },
  ], [subscriptions, onAssignBulk]);

  // DataTable column definitions for Subscriptions
  const subscriptionColumns: ColumnDefinition<Subscription>[] = useMemo(() => [
    {
      id: 'organization',
      header: 'Organization',
      sortable: true,
      sortKey: 'organizationName',
      accessor: (sub) => (
        <div className="flex items-center">
          <div className="h-10 w-10 flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
              <BuildingOfficeIcon className="h-5 w-5 text-purple-600" />
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-slate-900">
              {sub.organizationName || 'Unknown'}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'plan',
      header: 'Plan',
      sortable: true,
      sortKey: 'planId',
      accessor: (sub) => {
        const plan = plans.find(p => p.planId === sub.planId);
        return (
          <span className="text-sm text-slate-900">
            {plan?.name || sub.planId || 'No Plan'}
          </span>
        );
      }
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      sortKey: 'status',
      accessor: (sub) => <StatusBadge status={sub.status} />
    },
    {
      id: 'users',
      header: 'Users',
      sortable: false,
      accessor: (sub) => {
        const clinicalUsers = sub.userCounts?.clinical || 0;
        const staffUsers = sub.userCounts?.staff || 0;
        const totalUsers = sub.userCounts?.total || clinicalUsers + staffUsers;

        if (totalUsers === 0) {
          return <span className="text-sm text-slate-400">-</span>;
        }

        return (
          <div className="text-sm">
            <div className="flex items-center gap-1">
              <span className="font-medium text-slate-700">{clinicalUsers}</span>
              <span className="text-slate-400 text-xs">clinical</span>
            </div>
            {staffUsers > 0 && (
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <span>{staffUsers}</span>
                <span>staff</span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: 'usage',
      header: 'Usage',
      sortable: true,
      sortKey: 'usage',
      accessor: (sub) => {
        const plan = plans.find(p => p.planId === sub.planId);
        const clinicalUsers = sub.userCounts?.clinical || 0;
        const encountersPerUser = plan?.included?.encountersPerClinicalUser || 0;
        const isPerUserModel = encountersPerUser > 0 && clinicalUsers > 0;

        // Calculate effective limit based on model type
        const effectiveLimit = isPerUserModel
          ? sub.effectiveLimits?.totalEncountersThisMonth || (clinicalUsers * encountersPerUser)
          : sub.limits?.maxEncountersPerMonth ?? 0;

        const used = sub.usage?.currentEncountersThisMonth ?? 0;
        const isUnlimited = sub.limits?.maxEncountersPerMonth === -1;

        return (
          <div className="w-48">
            <UsageProgressBar
              used={used}
              limit={effectiveLimit}
              label=""
              isUnlimited={isUnlimited}
              compact
            />
            {isPerUserModel && !isUnlimited && (
              <p className="text-xs text-gray-500 mt-1">
                {clinicalUsers} user{clinicalUsers !== 1 ? 's' : ''} × {encountersPerUser} = {effectiveLimit.toLocaleString()}
              </p>
            )}
          </div>
        );
      }
    },
    {
      id: 'periodEnds',
      header: 'Period Ends',
      sortable: true,
      sortKey: 'periodEnds',
      accessor: (sub) => (
        <span className="text-sm text-slate-500">
          {sub.period?.currentPeriodEnd
            ? new Date(sub.period.currentPeriodEnd).toLocaleDateString()
            : '-'}
        </span>
      )
    }
  ], [plans]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-2">
        <h3 className="text-lg font-semibold text-slate-800">Organization Subscriptions</h3>
        {selectedIds.size > 0 && (
          <span className="text-sm text-purple-600 font-medium">
            {selectedIds.size} selected
          </span>
        )}
      </div>

      {/* Bulk Actions Bar */}
      <AnimatePresence>
        {selectedIds.size > 0 && (
          <BulkActionsBar
            selectedIds={selectedIds}
            items={sortedSubscriptions}
            getItemId={(s) => s.organizationId || s.id || ''}
            actions={bulkActions}
            onClearSelection={() => setSelectedIds(new Set())}
            entityName="subscription"
            theme="purple"
          />
        )}
      </AnimatePresence>

      <DataTable
        data={sortedSubscriptions}
        columns={subscriptionColumns}
        actions={rowActions}
        loading={loading}
        sortable={true}
        sortState={sortState}
        onSortChange={setSortState}
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        getRowId={(sub) => sub.organizationId || sub.id || `sub-${Math.random()}`}
        emptyMessage="No organizations have subscriptions yet."
        emptyIcon={<BuildingOfficeIcon className="h-12 w-12 text-slate-300" />}
        hoverable
        striped={false}
        ariaLabel="Organization subscriptions table"
      />
    </div>
  );
};

// Status Badge
const StatusBadge: React.FC<{ status?: Subscription['status'] }> = ({ status }) => {
  const styles: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    trial: 'bg-blue-100 text-blue-700',
    suspended: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-red-100 text-red-700',
    pending: 'bg-slate-100 text-slate-700',
    unknown: 'bg-gray-100 text-gray-700'
  };

  const displayStatus = status || 'unknown';

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[displayStatus] || styles.unknown}`}>
      {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
    </span>
  );
};

// Analytics Tab Content
const AnalyticsTabContent: React.FC<{
  subscriptions: Subscription[];
  plans: SubscriptionPlan[];
  loading?: boolean;
}> = ({ subscriptions, plans, loading }) => {
  // Loading state
  if (loading) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-xl p-12 border border-slate-200/50 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-500 border-t-transparent mb-4" />
        <p className="text-slate-600">Loading analytics...</p>
      </div>
    );
  }

  // Empty state - no subscriptions
  if (!subscriptions || subscriptions.length === 0) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-xl p-12 border border-slate-200/50 flex flex-col items-center justify-center text-center">
        <ChartBarIcon className="w-12 h-12 text-slate-400 mb-4" />
        <h3 className="text-lg font-semibold text-slate-700 mb-2">No Analytics Data</h3>
        <p className="text-slate-500 max-w-md">
          No subscriptions found to analyze. Assign subscription plans to organizations to see usage analytics.
        </p>
      </div>
    );
  }

  // Calculate analytics
  const planDistribution = plans.map(plan => {
    const count = subscriptions.filter(s => s.planId === plan.planId).length;
    return { plan, count };
  }).filter(p => p.count > 0).sort((a, b) => b.count - a.count);

  const totalEncounters = subscriptions.reduce((sum, s) => sum + (s.usage?.currentEncountersThisMonth ?? 0), 0);
  const totalMinutes = subscriptions.reduce((sum, s) => sum + (s.usage?.currentTranscriptionMinutes ?? 0), 0);
  const inOverage = subscriptions.filter(s =>
    (s.overageTracking?.encounterOverageCount ?? 0) > 0 || (s.overageTracking?.minuteOverageCount ?? 0) > 0
  ).length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Plan Distribution */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200/50">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Plan Distribution</h3>
        {planDistribution.length === 0 ? (
          <div className="text-center py-6 text-slate-500">
            <p>No plans currently assigned to organizations.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {planDistribution.map(({ plan, count }) => (
              <div key={plan.planId} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-3 h-3 rounded-full ${tierClasses(
                      TIER_DOT_CLASSES,
                      plan.tier,
                      'bg-slate-500'
                    )}`}
                  />
                  <span className="text-sm font-medium text-slate-700">{plan.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-24 bg-slate-200 rounded-full h-2">
                    <div
                      className="bg-purple-500 h-2 rounded-full"
                      style={{ width: `${(count / subscriptions.length) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-slate-800 w-8 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Usage Summary */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200/50">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Platform Usage Summary</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-slate-600 mb-1">
              <DocumentTextIcon className="w-4 h-4" />
              <span className="text-sm">Total Encounters</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{totalEncounters.toLocaleString()}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-slate-600 mb-1">
              <ClockIcon className="w-4 h-4" />
              <span className="text-sm">Total Minutes</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{totalMinutes.toLocaleString()}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-slate-600 mb-1">
              <UsersIcon className="w-4 h-4" />
              <span className="text-sm">Active Orgs</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">
              {subscriptions.filter(s => s.status === 'active').length}
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-slate-600 mb-1">
              <CurrencyDollarIcon className="w-4 h-4" />
              <span className="text-sm">In Overage</span>
            </div>
            <p className="text-2xl font-bold text-red-600">{inOverage}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlansPage;
